import React, { FC } from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { HsDataWell, HsReportCard, HsReportGrid } from "./pages";
import useLiveSamples from "../../common/hooks/useLiveSamples";

/**
 * The emails boring is checking right now, as a HubSpot report card (the
 * Insights page's card, data well and line chart): the queue now, then how
 * it moved over the last two minutes, sampled every two seconds while the
 * page is open.
 */

const LINE = "#fea58e";
const AXIS_TICK = { fill: "#333333", fontSize: 11, fontWeight: 300, fontFamily: "Lexend Deca, Helvetica, Arial, sans-serif" };
const AXIS_TITLE = { fill: "#333333", fontSize: 12, fontWeight: 600, fontFamily: "Lexend Deca, Helvetica, Arial, sans-serif" };

/** Four even steps of 1, 2 or 5 times a power of ten, the top a little above the highest point. */
const axisTicks = (max: number): number[] => {
  const want = Math.max(1, (max * 1.1) / 4);
  const power = 10 ** Math.floor(Math.log10(want));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * power).find((x) => x >= want && Number.isInteger(x)) ?? Math.ceil(want);
  return [0, 1, 2, 3, 4].map((i) => i * step);
};

const LiveTooltip: FC<{ active?: boolean; label?: string; payload?: { value: number }[] }> = ({ active, label, payload }) =>
  active && payload?.length ? (
    <div className="hs-chart-tooltip">
      <strong>{label}</strong>
      <div>
        <span style={{ color: LINE }}>●</span> (Count) Emails in queue: <b>{payload[0].value.toLocaleString()}</b>
      </div>
    </div>
  ) : null;

const LiveVerificationsCard: FC<{ inQueue: number }> = ({ inQueue }) => {
  const points = useLiveSamples({ value: inQueue });

  const ticks = axisTicks(Math.max(...points.map((p) => Number(p.value))));

  return (
    <div className="hs-live-report">
      <HsReportGrid>
        <HsReportCard
          title="Live verifications"
          info="Emails waiting to be checked or being checked now, across your team."
          chips={["Live", "Last 2 minutes"]}
          span={3}
          rows={2}
        >
          <HsDataWell items={[{ label: "(Count) Emails in queue", value: inQueue.toLocaleString() }]} />
          <div className="hs-chart">
            <div style={{ position: "absolute", inset: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={points} margin={{ top: 16, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid vertical={false} stroke="#cccccc" strokeDasharray="4 3" />
                  <XAxis dataKey="date" tick={AXIS_TICK} tickLine={false} axisLine={{ stroke: "#cccccc" }} minTickGap={48} height={24} />
                  <YAxis
                    tick={AXIS_TICK}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    width={56}
                    domain={[0, ticks[ticks.length - 1]]}
                    ticks={ticks}
                    label={{ value: "(Count) Emails", angle: -90, position: "insideLeft", offset: 10, style: { ...AXIS_TITLE, textAnchor: "middle" } }}
                  />
                  <Tooltip content={<LiveTooltip />} cursor={{ stroke: "#cccccc" }} />
                  <Line type="linear" dataKey="value" stroke={LINE} strokeWidth={2} dot={false} activeDot={{ r: 4 }} isAnimationActive={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </HsReportCard>
      </HsReportGrid>
    </div>
  );
};

export default LiveVerificationsCard;
